import React from "react";
import {
  BlueBirdTheme, Button, Loading, Typography,
} from "@kitbird/core";
import Table from "../../../../../components/Table";
import useApprovalVendor from "../../hooks/useApprovalVendor";
import { ReactComponent as ViewIcon } from "../../../../../assets/icons/show_icon.svg";
import { ReactComponent as CheckIcon } from "../../../../../assets/icons/check_mark.svg";
import { ReactComponent as CrossIcon } from "../../../../../assets/icons/cross_mark.svg";
import { ColumnI } from "../../../../../components/Table/TableProps";
import { TabTypes } from "../../../../Transaction/CreateReceiptSubmission/enums";
import { ModalType } from "../../constants";

interface ApprovalVendorTableI {
  useCustomHook: ReturnType<typeof useApprovalVendor>;
}

const {
  danger, gray, primary, success,
} = BlueBirdTheme.colors;

const ApprovalVendorTable = ({ useCustomHook }: ApprovalVendorTableI) => {
  const {
    obsRef,
    meta,
    params,
    setParams,
    hiddenColumn,
    approvalVendorList,
    isFetching,
    isFetchingNextPage,
    setDetailRow,
    setOpen,
  } = useCustomHook;

  const renderVendorAddress = (item: any, row: Record<string, any>) => (
    <div className="ellipsis-text">
      <Typography level={6} color={gray.subtitle} fontWeight="medium">{item}</Typography>
      <br />
      <Typography level={7} color={gray.bodyText}>{row.vendor_address}</Typography>
    </div>
  );
  const renderVendorAccount = (item: any, row: Record<string, any>) => (
    <div className="ellipsis-text">
      <Typography level={6} color={gray.subtitle} fontWeight="medium">{row.bank_account_number} • {row.bank_account}</Typography>
      <br />
      <Typography level={7} color={gray.bodyText}>{item}</Typography>
    </div>
  );
  const renderAttachments = (item: any[] | any) => (
    <div className="ellipsis-text" style={{ width: 200 }}>
      {
        item.length
          ? `${item[0].file_name}${item.length > 1 ? ` +${item.length - 1}` : ""}`
          : `${item.file_text}${item.file_count > 1 ? ` +${item.file_count - 1}` : ""}`
      }
    </div>
  );

  const renderActions = (item: any, row: Record<string, any>) => (<div style={{ display: "flex", gap: 8 }}>
    {((isNeedApproval) => {
      if (isNeedApproval) {
        return (
          <>
            <Button
              style={{ padding: "8px 10px" }}
              backgroundColor={success[400]}
              onClick={(e) => {
                e.stopPropagation();
                setDetailRow(row);
                setOpen(ModalType.APPROVE);
              }}
            >
              <CheckIcon width={16} height={16} />
            </Button>
            <Button
              style={{ padding: "8px 10px" }}
              backgroundColor={danger[400]}
              onClick={(e) => {
                e.stopPropagation();
                setDetailRow(row);
                setOpen(ModalType.REJECT);
              }}
            >
              <CrossIcon width={16} height={16} />
            </Button>
          </>
        );
      } return (
        <Button
          style={{ padding: "8px 10px" }}
          backgroundColor={primary[300]}
          onClick={(e) => {
            e.stopPropagation();
            setDetailRow(row);
            setOpen(ModalType.DETAIL);
          }}
        >
          <ViewIcon width={16} height={16} />
        </Button>
      );
    })(params.status_approval === TabTypes.OPEN)}
  </div>);

  const columns: ColumnI[] = [
    {
      title: "Kode vendor",
      dataIndex: "vendor_code",
      key: "vendor_code",
      sort: true,
    },
    {
      title: "Nama & alamat vendor",
      dataIndex: "vendor_name",
      key: "vendor_name",
      sort: true,
      render: renderVendorAddress,
    },
    {
      title: "Tipe vendor",
      dataIndex: "type_vendor",
      key: "type_vendor",
      sort: true,
    },
    {
      title: "Detail rekening vendor",
      dataIndex: "bank_account_name",
      key: "bank_account_name",
      sort: true,
      render: renderVendorAccount,
    },
    {
      title: "Lampiran",
      dataIndex: "vendor_files",
      key: "vendor_files",
      render: renderAttachments,
    },
    {
      title: "Action",
      dataIndex: "id",
      key: "id",
      width: "108px",
      render: renderActions,
    },
  ];

  return (<>
    <Table
      withNumber
      dataSource={approvalVendorList}
      columns={columns}
      hiddenColumnKeys={hiddenColumn}
      onClickRow={(row) => {
        setDetailRow(row);
        setOpen(ModalType.DETAIL);
      }}
      sortConfig={{
        sortType: params.sort_type as "ASC" | "DSC" | "DEFAULT",
        sortBy: params.sort_by,
        onSort: (name, type) => setParams((curr) => ({ ...curr, sort_by: name, sort_type: type })),
      }}
      footer={
        <>
          {(!isFetching && meta.page < meta.total_pages) && <tfoot ref={obsRef} style={{ height: 5, width: "100%" }} />}
          {isFetchingNextPage && <Loading variant="circular" />}
        </>
      }
    />
  </>);
};

export default ApprovalVendorTable;
